import { Linkedin, Instagram, Facebook, Youtube, Mail, MapPin, FileDown } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToSection } from "../../lib/scroll";
import { useLang } from "../../i18n/LanguageContext";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={props.className} aria-hidden="true">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const SOCIALS = [
  { label: "LinkedIn", icon: Linkedin, tid: "footer-social-linkedin" },
  { label: "Instagram", icon: Instagram, tid: "footer-social-instagram" },
  { label: "TikTok", icon: TikTokIcon, tid: "footer-social-tiktok" },
  { label: "Facebook", icon: Facebook, tid: "footer-social-facebook" },
  { label: "YouTube", icon: Youtube, tid: "footer-social-youtube" },
];

export default function Footer() {
  const { t } = useLang();
  const c = t.footer;

  return (
    <footer className="border-t border-white/10 bg-vg-black" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-vg-muted">{c.claim}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-green">{c.kicker}</p>
            <div className="mt-8 flex gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href="#"
                  aria-label={s.label}
                  data-testid={s.tid}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-vg-muted transition-all duration-200 hover:border-vg-green hover:text-vg-green"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h3 className="font-display text-lg font-semibold uppercase tracking-wider text-vg-white">{c.sections}</h3>
            <ul className="mt-6 space-y-3">
              {c.links.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => scrollToSection(s.id)}
                    data-testid={`footer-link-${s.id}`}
                    className="text-sm text-vg-muted transition-colors duration-200 hover:text-vg-green"
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h3 className="font-display text-lg font-semibold uppercase tracking-wider text-vg-white">{c.contacts}</h3>
            <ul className="mt-6 space-y-4 text-sm text-vg-muted">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-vg-green" />
                <span>
                  <strong className="font-semibold text-vg-white">Co.Ge.S.Tur. Soc. Coop.</strong>
                  <br />
                  Misano Adriatico (RN) — Italia
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-vg-green" />
                <a href="mailto:info@evgpitalia.it" data-testid="footer-email" className="transition-colors hover:text-vg-green">
                  info@evgpitalia.it
                </a>
              </li>
            </ul>
            <div className="mt-6 border border-white/10 bg-vg-card p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-vg-muted">{c.pdfTitle}</p>
              <ul className="mt-3 space-y-2">
                {c.pdfLinks.map((l) => (
                  <li key={l.href}>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${l.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={l.tid}
                      className="inline-flex items-center gap-2 text-sm text-vg-muted transition-colors hover:text-vg-green"
                    >
                      <FileDown className="h-3.5 w-3.5" />
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 border border-white/10 bg-vg-card p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-vg-muted">{c.promoter}</p>
              <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-vg-white">{c.promoterName}</p>
              <p className="mt-1 text-xs text-vg-muted">{c.promoterSub}</p>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-xs text-vg-muted">{c.copyright}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-vg-muted">{c.promoted}</p>
        </div>
      </div>
    </footer>
  );
}
